import React from 'react';
import { cn } from './cn';

export type MetricTone = 'neutral' | 'brand' | 'safety' | 'recommendation' | 'maintenance';

export interface MetricCardProps {
  label: string;
  value: number | string;
  /** Optional trailing bit of context — e.g. "across 6 sections". */
  hint?: string;
  /** Icon rendered in the top-right. */
  icon?: React.ReactNode;
  tone?: MetricTone;
  className?: string;
  /** Emphasize — larger display number. */
  featured?: boolean;
}

const TONE: Record<MetricTone, { bg: string; ring: string; label: string; value: string; icon: string }> = {
  neutral: {
    bg: 'bg-surface-1', ring: 'ring-border-subtle',
    label: 'text-ink-subtle', value: 'text-ink', icon: 'bg-surface-2 text-ink-muted',
  },
  brand: {
    bg: 'bg-surface-1', ring: 'ring-brand/15',
    label: 'text-ink-subtle', value: 'text-ink', icon: 'bg-brand-soft text-brand',
  },
  safety: {
    bg: 'bg-sev-critical-soft/60', ring: 'ring-sev-critical/15',
    label: 'text-sev-critical-ink', value: 'text-sev-critical-ink',
    icon: 'bg-sev-critical-soft text-sev-critical',
  },
  recommendation: {
    bg: 'bg-sev-minor-soft/60', ring: 'ring-sev-minor/15',
    label: 'text-sev-minor-ink', value: 'text-sev-minor-ink',
    icon: 'bg-sev-minor-soft text-sev-minor',
  },
  maintenance: {
    bg: 'bg-sev-maintenance-soft/60', ring: 'ring-sev-maintenance/15',
    label: 'text-sev-maintenance-ink', value: 'text-sev-maintenance-ink',
    icon: 'bg-sev-maintenance-soft text-sev-maintenance',
  },
};

export const MetricCard: React.FC<MetricCardProps> = ({
  label, value, hint, icon, tone = 'neutral', className, featured = false,
}) => {
  const t = TONE[tone];
  return (
    <div
      className={cn(
        'relative rounded-2xl p-5 ring-1 ring-inset',
        'transition-transform duration-180 ease-premium',
        t.bg, t.ring,
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <span className={cn('text-overline uppercase', t.label)}>{label}</span>
        {icon && (
          <span
            className={cn(
              'h-8 w-8 rounded-lg flex items-center justify-center shrink-0',
              t.icon,
            )}
            aria-hidden
          >
            <span className="h-4 w-4 flex items-center justify-center">{icon}</span>
          </span>
        )}
      </div>
      <div
        className={cn(
          'mt-3 font-display font-semibold nums-tabular leading-none',
          featured ? 'text-display-lg' : 'text-display-sm',
          t.value,
        )}
        style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 50" }}
      >
        {value}
      </div>
      {hint && <p className={cn('mt-2 text-caption', t.label)}>{hint}</p>}
    </div>
  );
};
